class VideoTestimonialsCarousel extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-video-testimonials-track]');
    this.prevButton = this.querySelector('.video-testimonials__arrow--prev');
    this.nextButton = this.querySelector('.video-testimonials__arrow--next');
    this.speed = parseInt(this.dataset.speed, 10) || 5000;
    this.autoplayEnabled = this.dataset.autoplay !== 'false';

    const originalSlides = this.track ? Array.from(this.track.children) : [];
    this.slideCount = originalSlides.length;
    if (!this.track || this.slideCount < 2) return;

    // Carrusel infinito: se clona el set completo de videos antes y
    // después del real, así siempre hay contenido de sobra para
    // desplazarse en cualquier dirección. Al llegar a un video clonado,
    // saltamos (sin animación, invisible al ojo) al video real
    // equivalente — nunca se ve el "corte" ni un rebote hacia atrás.
    // Los clones arrancan pausados: el IntersectionObserver de abajo es
    // quien decide qué videos reproducir, para no tener el doble (o
    // triple) de videos sonando/cargando fuera de pantalla a la vez.
    const cloneSet = () =>
      originalSlides.map((slide) => {
        const clone = slide.cloneNode(true);
        clone.setAttribute('aria-hidden', 'true');
        clone.removeAttribute('data-shopify-editor-block');
        return clone;
      });

    cloneSet()
      .reverse()
      .forEach((clone) => this.track.insertBefore(clone, this.track.firstChild));
    cloneSet().forEach((clone) => this.track.appendChild(clone));

    // Arranca posicionado justo al inicio del bloque real (después del
    // primer set clonado), sin animación.
    const slides = Array.from(this.track.children);
    this.track.scrollLeft = slides[this.slideCount].offsetLeft;

    this.prevButton?.addEventListener('click', () => {
      this.stop();
      this.advance(-1);
      this.play();
    });
    this.nextButton?.addEventListener('click', () => {
      this.stop();
      this.advance(1);
      this.play();
    });

    this.addEventListener('mouseenter', () => this.stop());
    this.addEventListener('mouseleave', () => this.play());
    this.addEventListener('focusin', () => this.stop());
    this.addEventListener('focusout', () => this.play());

    // Solo reproduce los videos que realmente están visibles dentro del
    // carrusel; los que quedan fuera (clones del loop infinito, o slides
    // reales que aún no se han desplazado a la vista) se pausan, para no
    // gastar datos ni batería reproduciendo lo que no se ve.
    this.videoObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const video = entry.target;
          video.muted = true;
          if (entry.isIntersecting) {
            video.play?.().catch(() => {});
          } else {
            video.pause?.();
          }
        });
      },
      { root: this.track, threshold: 0.5 }
    );
    this.track.querySelectorAll('video').forEach((video) => this.videoObserver.observe(video));

    this.play();
  }

  getCurrentIndex() {
    const slides = Array.from(this.track.children);
    return slides.reduce((closest, slide, index) => {
      const closestDistance = Math.abs(slides[closest].offsetLeft - this.track.scrollLeft);
      const distance = Math.abs(slide.offsetLeft - this.track.scrollLeft);
      return distance < closestDistance ? index : closest;
    }, 0);
  }

  advance(direction = 1) {
    const track = this.track;
    const slides = Array.from(track.children);
    if (!slides.length) return;

    const currentIndex = this.getCurrentIndex();
    const nextIndex = currentIndex + direction;
    if (nextIndex < 0 || nextIndex >= slides.length) return;

    track.scrollTo({ left: slides[nextIndex].offsetLeft, behavior: 'smooth' });

    // Después de que termine la animación, si quedamos sobre un video
    // clonado, saltamos al real equivalente al otro extremo del set.
    clearTimeout(this._loopTimeout);
    this._loopTimeout = setTimeout(() => this.normalizePosition(nextIndex), 600);
  }

  normalizePosition(index) {
    const track = this.track;
    const slides = Array.from(track.children);
    const total = this.slideCount;

    if (index < total) {
      track.scrollLeft = slides[index + total].offsetLeft;
    } else if (index >= total * 2) {
      track.scrollLeft = slides[index - total].offsetLeft;
    }
  }

  play() {
    clearInterval(this.timer);
    // Si "Rotación automática" está apagada desde el personalizador, no
    // arranca el temporizador — las flechas manuales y los videos en
    // bucle siguen funcionando igual, solo no avanza solo.
    if (!this.autoplayEnabled) return;
    this.timer = setInterval(() => this.advance(1), this.speed);
  }

  stop() {
    clearInterval(this.timer);
  }

  disconnectedCallback() {
    clearInterval(this.timer);
    clearTimeout(this._loopTimeout);
    this.videoObserver?.disconnect();
  }
}

customElements.define('video-testimonials-carousel', VideoTestimonialsCarousel);
